import { Component, Inject, OnInit } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { StorageService } from 'src/app/core/store/storage/storage.service';
import { FeedbackSnackbarService } from 'src/app/core/utilities/feedback-snackbar/feedback-snackbar.service';
import { UserFeedbackState } from 'src/app/core/utilities/user-feedback-state/user-feedback-state';
import { CustomerFacadeService } from 'src/app/facades/customer/customer-facade.service';
import { SettingsFacadesService } from 'src/app/facades/settings/settings-facades.service';
import { UserFacadeService } from 'src/app/facades/user/user-facade.service';
import * as UTIF from 'utif2'; // Install: npm install utif2

@Component({
  selector: 'app-download-poster',
  templateUrl: './download-poster.component.html',
  styleUrls: ['./download-poster.component.scss']
})
export class DownloadPosterComponent implements OnInit {
  userId: number;
  poster: SafeUrl | string;
  private currentObjectURL: string;
  userFeedback = new UserFeedbackState();
  isBtnLoad: boolean = false;
  stylistName: any;
  isDarkMode: boolean;
  customerName: any;
  customerEmail: any;
  constructor(private settingFacade: SettingsFacadesService,  
    private bottomSheet: MatBottomSheetRef<DownloadPosterComponent>, 
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
    private storageService: StorageService,
    private userFacade: UserFacadeService,
    private snackBar: FeedbackSnackbarService,
    private customerFacadeService: CustomerFacadeService,
    private sanitizer: DomSanitizer) { 
      this.isDarkMode = localStorage.getItem('darkMode') === 'true';
      const retrievedUserData = this.storageService.getUserData();
      if (!retrievedUserData?.sId) {
        return;
      }
      this.userId = retrievedUserData.sId;
      this.customerName = data.customerName;
      this.customerEmail = data.customerEmail;
      this.previewPoster();
      this.getStylistById();
    }

  ngOnInit(): void {
  }

  getStylistById(){
    this.userFacade.getUser(this.userId).subscribe({
      next: (response: any) => {
        this.stylistName = response.firstName;
      },
      error: (error) => {
      }
    });
  }

  radioSelected(event){
    this.isDarkMode = event.value;
    this.previewPoster();
  }

  previewPoster() {
    this.userFeedback.startLoading();
    
    // Clean up previous URL if exists
    if (this.currentObjectURL) {
      URL.revokeObjectURL(this.currentObjectURL);
    }
    
    const data = {
      "stylistId": this.userId,
      "isDarkModeTemplate": this.isDarkMode
    };

    this.settingFacade.downloadQrPoster(data).subscribe({
      next: (response: Blob) => {  
        if (response.type === 'image/tiff' || response.type === 'image/tif') {
          this.convertTiffToDataUrl(response);
        } else {
          // Handle other image formats normally
          this.currentObjectURL = URL.createObjectURL(response);
          this.poster = this.sanitizer.bypassSecurityTrustUrl(this.currentObjectURL);
          this.userFeedback.stopLoading();
        }
      },
      error: (error) => {
        this.userFeedback.stopLoading();
      }
    });
  }

  private convertTiffToDataUrl(tiffBlob: Blob) {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const arrayBuffer = reader.result as ArrayBuffer;
        const uint8Array = new Uint8Array(arrayBuffer);
        // Decode TIFF using imported UTIF
        const ifds = UTIF.decode(uint8Array);
        if (ifds.length === 0) {
          throw new Error('No images found in TIFF file');
        }
        const firstImage = ifds[0];
        
        // Decode the image data
        UTIF.decodeImage(uint8Array, firstImage);
        
        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = firstImage.width;
        canvas.height = firstImage.height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Could not get canvas context');
        }
        
        const imageData = ctx.createImageData(firstImage.width, firstImage.height);
        
        // Convert to RGBA
        const rgba = UTIF.toRGBA8(firstImage);
        imageData.data.set(rgba);
        
        ctx.putImageData(imageData, 0, 0);
        
        // Convert canvas to data URL
        const dataUrl = canvas.toDataURL('image/png');
        
        this.poster = this.sanitizer.bypassSecurityTrustUrl(dataUrl);
        this.userFeedback.stopLoading();
        
      } catch (error) {
        // Fallback: try to display as blob URL (might not work but worth trying)
        try {
          this.currentObjectURL = URL.createObjectURL(tiffBlob);
          this.poster = this.sanitizer.bypassSecurityTrustUrl(this.currentObjectURL);
        } catch (fallbackError) {
        }
        
        this.userFeedback.stopLoading();
      }
    };
    
    reader.onerror = (error) => {
      this.userFeedback.stopLoading();
    };
    reader.readAsArrayBuffer(tiffBlob);
  }

  ngOnDestroy() {
    if (this.currentObjectURL) {
      URL.revokeObjectURL(this.currentObjectURL);
    }
  }

  downloadPoster() {
    this.isBtnLoad = true;
    const data = {
      stylistId: this.userId,
      isDarkModeTemplate: this.isDarkMode
    };
  
    this.settingFacade.downloadQrPoster(data).subscribe({
      next: (response: Blob) => {
        // Validate that it is a TIFF file
        const isTiff = response.type === 'image/tiff' || response.type === 'image/tif';
        if (!isTiff) {
          this.isBtnLoad = false;
          this.snackBar.errorSnackbar('Unexpected file format. Expected a TIFF image.');
          return;
        }
        this.snackBar.successSnackbar('Poster downloaded successfully.');
        // Convert TIFF to JPG using UTIF (don't show success here, only after conversion)
        this.convertTiffToJpgWithUTIF(response);
      },
      error: (error) => {
        this.isBtnLoad = false;
        this.snackBar.errorSnackbar('Failed to download poster.');
      }
    });
  }
  
  private convertTiffToJpgWithUTIF(tiffBlob: Blob) {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        // Get ArrayBuffer from the TIFF blob
        const buffer = e.target?.result as ArrayBuffer;
        
        // Parse TIFF using UTIF
        const ifds = UTIF.decode(buffer);
        const firstImage = ifds[0];
        
        // Decode the image data
        UTIF.decodeImage(buffer, firstImage);
        
        // Create canvas and get RGBA data
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = firstImage.width;
        canvas.height = firstImage.height;
        
        // Create ImageData from UTIF RGBA data
        const imageData = ctx!.createImageData(firstImage.width, firstImage.height);
        const rgba = new Uint8Array(firstImage.data);
        imageData.data.set(rgba);
        
        // Put image data on canvas
        ctx!.putImageData(imageData, 0, 0);
        
        // Convert canvas to JPG blob
        canvas.toBlob((jpgBlob) => {
          if (jpgBlob) {
            // Create download link for JPG
            const url = URL.createObjectURL(jpgBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${this.stylistName}-poster.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
          
          // Always reset loading state and show message (success or error)
          this.isBtnLoad = false;
          this.bottomSheet.dismiss();
        }, 'image/jpeg', 0.9); // 90% quality
        
      } catch (error) {
        this.isBtnLoad = false;
        this.snackBar.errorSnackbar('Failed to process TIFF image.');
      }
    };
    
    reader.onerror = () => {
      this.isBtnLoad = false;
      this.snackBar.errorSnackbar('Failed to read TIFF file.');
    };
    
    reader.readAsArrayBuffer(tiffBlob);
  }

  close() {
    this.bottomSheet.dismiss();
  }

  sendPoster(){
    this.isBtnLoad = true;
    let value = {
      stylistId: this.userId,
      customerName: this.customerName,
      customerEmail:this.customerEmail,
      isDarkModeTemplate: this.isDarkMode
    };
    this.customerFacadeService.sendPosterToCus(value).subscribe({
      next: (response) => {
        this.isBtnLoad = false;
        this.snackBar.successSnackbar("Poster Sent Successfully");
        this.bottomSheet.dismiss();
      },
      error: (error) => {
        this.snackBar.errorSnackbar("Something went wrong, please try again");
        this.isBtnLoad = false;
      },
    });
  }

}
